import axios, { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { RedirectType, redirect } from 'next/navigation';
import axiosRetry from 'axios-retry';
import { rollbarInstance } from './rollbar';
import { userStore } from '@stores/UserStore';
import { isPresent } from '@farmai-shared/LFUtils';
interface ErrorResponse {
  code?: string;
  message?: string;
  extra?: {
    [k: string]: any;
  };
}
export type AgroAPIError = AxiosError<ErrorResponse>;
const shouldRetry = error => axiosRetry.isNetworkError(error) || axiosRetry.isRetryableError(error) || error.code === 'ECONNABORTED' || error.response && error.response.status === 429;
const isAuthError = error => error.response && error.response.status === 401 && ['token_expired', 'access_denied'].includes(error.response.data.code);
const getFirebaseToken = () => new Promise<string>((resolve, reject) => {
  onAuthStateChanged(getAuth(), user => {
    if (user) {
      return user.getIdToken().then(tok => resolve(tok));
    }
    return reject(null);
  });
});
axiosRetry(axios, {
  retries: 10,
  retryCondition: error => isAuthError(error) || shouldRetry(error),
  retryDelay: axiosRetry.exponentialDelay,
  onRetry: (retryCount, error) => {
    const originalRequest = error.config;
    if (isAuthError(error) && retryCount < 10) {
      return getFirebaseToken().then(fbToken => {
        userStore.setFirebaseToken(fbToken);
      }).then(() => userStore.getAgroApiToken()?.then(async newToken => {
        if (newToken && isPresent(originalRequest)) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken.access_token}`;
          await axios(originalRequest); // onRetry must return void
        }
      }).catch(async error => {
        if (error?.response?.status == 404) {
          await userStore.createUser();
        }
      })).catch(() => {
        redirect('/login', RedirectType.push);
      });
    }
    rollbarInstance.error(error, {
      url: originalRequest?.url,
      method: originalRequest?.method,
      data: originalRequest?.data,
      status: error.response?.status,
      code: (error.response?.data as ErrorResponse)?.code || 'NONE'
    });
    return Promise.reject(error);
  }
});
export async function callAgroApi<T>(path: string, method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT', data = {}, config: Pick<AxiosRequestConfig, 'onUploadProgress'> = {}): Promise<AxiosResponse<T>> {
  const expiresAt = userStore.agroApiToken?.expires_at;
  if (!expiresAt) {
    await getFirebaseToken().then(fbToken => userStore.setFirebaseToken(fbToken)).then(() => userStore.getAgroApiToken());
  }
  if (isPresent(expiresAt) && expiresAt < Date.now()) {
    await getFirebaseToken().then(fbToken => userStore.setFirebaseToken(fbToken)).then(() => userStore.getAgroApiToken());
  }
  return axios.request<T>({
    ...config,
    method: method,
    url: `${process.env.NEXT_PUBLIC_AGROAPI_URL}${path}`,
    data: data,
    headers: {
      Authorization: `Bearer ${userStore.agroApiToken?.access_token}`,
      'X-AGROAPI-VERSION': '1.51.0'
    }
  });
}